import * as React from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Loader2Icon } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { toast } from "sonner";

import { checkIsPaidPlan } from "@degaus/shared/plans";

import { appendSubscriptionSuccessParams } from "@/common/google-ads";
import { PaywallStep } from "@/features/onboarding/components/PaywallStep";
import { api } from "@/trpc/react";

export const Route = createFileRoute(
  "/_authenticated/(wonda)/_wonda/onboarding/paywall",
)({
  beforeLoad: async ({ context }) => {
    const answers =
      await context.trpcQueryUtils.account.getWelcomeAnswers.ensureData();
    if (!answers?.tools) {
      throw redirect({ to: "/onboarding/tools" });
    }
    if (!answers?.platforms) {
      throw redirect({ to: "/onboarding/platforms" });
    }
    const plan =
      await context.trpcQueryUtils.payments.getCurrentAccountPlan.ensureData();
    if (checkIsPaidPlan(plan?.planCode ?? null)) {
      throw redirect({ to: "/onboarding/guide" });
    }
    // The org-member skip is handled in the component, not here. An uncached
    // organization.list round-trip in beforeLoad held this route pending long
    // enough for RouterContextSyncInner's router.update() to tear down the
    // loadPromise while the accountId propagated into React state, blanking
    // the page to the near-black body bg until a manual refresh (same class of
    // bug as fix 0ebf2109d). The guide step reads org membership in the
    // component for the same reason.
  },
  component: PaywallRoute,
});

function PaywallRoute() {
  const navigate = useNavigate();
  const posthog = usePostHog();

  // Org members shouldn't be asked to pay during onboarding: their org admin
  // handles billing. Read membership here (not in beforeLoad) so the route's
  // beforeLoad stays short and can't trigger the pending-loadPromise teardown
  // that blanked this page. Mirrors the guide step.
  const { data: orgList } = api.organization.list.useQuery(undefined, {
    staleTime: 60_000,
  });
  const isOrgMember = (orgList?.length ?? 0) > 0;

  React.useEffect(() => {
    if (isOrgMember) {
      void navigate({ to: "/onboarding/guide", replace: true });
    }
  }, [isOrgMember, navigate]);

  const changePlan = api.billing.changePlan.useMutation({
    onSuccess: (data: { url: string }) => {
      window.location.assign(data.url);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  React.useEffect(() => {
    // Only count a view once the paywall is actually shown to a non-org user:
    // confirmed org members are redirected away, so firing this for them would
    // log a paywall they never see. A late-resolving org member may fire this
    // once during the brief pre-confirmation window: accepted tradeoff.
    if (isOrgMember) return;
    const STORAGE_KEY = "welcome-paywall-viewed";
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(STORAGE_KEY)) return;
    window.sessionStorage.setItem(STORAGE_KEY, "1");
    posthog.capture("welcome paywall viewed", { variant: "hardPaywall" });
  }, [posthog, isOrgMember]);

  const handleStartPlan = React.useCallback(
    (targetPlanCode: string) => {
      if (changePlan.isPending) return;
      posthog.capture("welcome paywall start pro clicked", {
        planCode: targetPlanCode,
        variant: "hardPaywall",
      });
      changePlan.mutate({
        planCode: targetPlanCode,
        interval: "month",
        successUrl: appendSubscriptionSuccessParams(
          `${window.location.origin}/onboarding/guide`,
        ),
        cancelUrl: `${window.location.origin}/onboarding/paywall`,
      });
    },
    [changePlan, posthog],
  );

  // A confirmed org member is being redirected to the guide by the effect
  // above, so show a lightweight loader instead of flashing the paywall and its
  // checkout button at someone whose org admin handles billing. Best-effort:
  // unknown/loading/error membership falls through to the paywall (no trap).
  if (isOrgMember) {
    return (
      <div className="flex min-h-[calc(100dvh-var(--header-height,80px)-var(--footer-height,60px))] items-center justify-center">
        <Loader2Icon
          className="size-6 animate-spin"
          style={{ color: "var(--cli-text-muted)" }}
        />
      </div>
    );
  }

  return (
    <PaywallStep
      isPending={changePlan.isPending}
      onStartPlan={handleStartPlan}
      onBack={() => {
        void navigate({ to: "/onboarding/platforms" });
      }}
    />
  );
}
